import { useStoredState } from "@/hooks/useStoredState";
import { ToolShell } from "@/components/tools/ToolShell";
import { AutoTextarea, Pills, TextField } from "@/components/tools/fields";
import { RowsEditor, SubHead, blankRows } from "@/components/tools/blocks";
import { LettersBlock } from "@/components/tools/ParentLetters";

const MEETING_TEXT = [
  { k: "participants", label: "Participants" },
  { k: "concerns", label: "Concerns raised" },
  { k: "strengths", label: "Strengths discussed" },
];
const ACTION_COLS = [
  { k: "action", label: "Action" },
  { k: "who", label: "By whom" },
  { k: "date", label: "Date", type: "date" },
];
const AUDIT = ["Parents feel listened to", "Parents understand provision", "Parents know who to contact", "Parents contribute to reviews", "Parents receive regular updates"];
const OPTS = [1, 2, 3, 4, 5].map((n) => ({ v: n, label: String(n) }));

export const ParentPartnership = () => {
  const [data, setData, reset] = useStoredState("parent-partnership-v3", { meeting: {}, agreed: blankRows(2), audit: {}, actions: "" });
  const m = data.meeting || {};
  const setM = (k, v) => setData((d) => ({ ...d, meeting: { ...d.meeting, [k]: v } }));
  const total = AUDIT.reduce((s, _, i) => s + (data.audit?.[i] || 0), 0);
  return (
    <ToolShell id="parent-partnership" onReset={reset}>
      <SubHead letter="A" title="Parent Meeting Record">Do not record the child's name. Use a school reference only if you need one.</SubHead>
      <div className="max-w-xs"><TextField id="pp-m-date" label="Date" type="date" value={m.date} onChange={(v) => setM("date", v)} testid="pp-m-date" /></div>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        {MEETING_TEXT.map((f) => <AutoTextarea key={f.k} id={`pp-m-${f.k}`} label={f.label} rows={3} value={m[f.k]} onChange={(v) => setM(f.k, v)} testid={`pp-m-${f.k}`} />)}
      </div>
      <h5 className="mb-3 mt-6 font-bold">Agreed actions</h5>
      <RowsEditor name="pp-agreed" rows={data.agreed} setRows={(v) => setData((d) => ({ ...d, agreed: v }))} cols={ACTION_COLS} addLabel="Add an action" grid="sm:grid-cols-3" />
      <div className="mt-4 max-w-xs"><TextField id="pp-m-review" label="Review date" type="date" value={m.review} onChange={(v) => setM("review", v)} testid="pp-m-review" /></div>

      <SubHead letter="B" title="Parent Engagement Audit">Rate each statement from 1 to 5.</SubHead>
      <ol className="divide-y divide-line border-y border-line">
        {AUDIT.map((t, i) => (
          <li key={t} className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
            <p className="font-bold">{t}</p>
            <Pills legend={`Rating for: ${t}`} name={`pp-a-${i}`} options={OPTS} value={data.audit?.[i]}
              onChange={(v) => setData((d) => ({ ...d, audit: { ...d.audit, [i]: v } }))} testid={`pp-audit-${i}`} />
          </li>
        ))}
      </ol>
      <p className="mt-4 font-bold" aria-live="polite" data-testid="pp-audit-total">Total: {total} / 25</p>
      <div className="mt-4">
        <AutoTextarea id="pp-actions" label="Actions" rows={3} value={data.actions} onChange={(v) => setData((d) => ({ ...d, actions: v }))} testid="pp-actions" />
      </div>

      <SubHead letter="C" title="Letter templates">Ready-made wording you can edit, copy or print.</SubHead>
      <LettersBlock />
    </ToolShell>
  );
};
